import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, Boat, FileText } from "@phosphor-icons/react";

const PAGE_SIZE = 50;

const StatusPill = ({ status, round }) => {
  const color = status === "closed"
    ? "border-black/20 bg-[#0B1929] text-white"
    : round > 1
      ? "border-klein-blue text-[#22D3EE]"
      : "border-black/30 text-black";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold border ${color}`}>
      {status === "closed" ? "Closed" : `Open · R${round}`}
    </span>
  );
};

export default function FreightList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/auctions", { params: { page: 1, page_size: PAGE_SIZE } });
        if (cancelled) return;
        setItems((data.items || []).filter((a) => a.type === "freight"));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="freight-list-page">
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
              <Boat size={12} weight="bold" /> Freight Procurement
            </div>
            <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tightest leading-none">
              Freight <span className="klein-blue">Auctions</span>
            </h1>
            <p className="text-base text-neutral-600 mt-6 max-w-2xl leading-relaxed">
              Reverse auctions for container shipments. Bidders commit a rate <strong>and</strong> an
              assured vessel + ETD per shipment. At close, containers are allocated to the cheapest
              bidder honouring each shipment&apos;s container-pickup date.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 justify-end">
            {isAdmin && (
              <Link to="/freight/new">
                <Button className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp" data-testid="create-freight-cta">
                  <Plus size={16} weight="bold" className="mr-2" />
                  New Freight Auction
                </Button>
              </Link>
            )}
          </div>
        </div>

        <section data-testid="freight-auctions-section">
          <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
            <h2 className="font-display font-extrabold text-2xl tracking-tightest">All Freight Auctions</h2>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{items.length} total</span>
          </div>
          {loading ? (
            <div className="py-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-black/15 p-10 text-center mt-4">
              <div className="text-sm text-neutral-600">
                No freight auctions yet. {isAdmin && "Create one to start procuring container shipments."}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-black/10 border-b border-black/10">
              {items.map((a) => (
                <Link
                  to={`/auctions/${a.id}`}
                  key={a.id}
                  className="grid grid-cols-12 gap-4 py-5 hover:bg-neutral-50 transition-colors group items-center px-2"
                  data-testid={`freight-row-${a.id}`}
                >
                  <div className="col-span-12 md:col-span-6">
                    <div className="font-display font-extrabold text-xl tracking-tightest leading-tight">{a.title}</div>
                    <div className="text-sm text-neutral-600 mt-1 line-clamp-1">{a.description}</div>
                    <div className="flex gap-2 mt-1.5 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                      <span className="inline-flex items-center gap-1"><FileText size={10} weight="bold" /> {(a.shipments || []).length} shipments · 1 container each</span>
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Round</div>
                    <div className="font-mono-plex text-lg">R{a.current_round}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
                      {a.is_creator ? "Coverage" : "Your bid?"}
                    </div>
                    <div className="font-mono-plex text-lg">
                      {a.is_creator
                        ? `${Object.values(a.per_shipment_bid_count || {}).filter((n) => n > 0).length}/${(a.shipments || []).length}`
                        : (a.my_current_round_bid ? "Yes" : "—")}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-3">
                    <StatusPill status={a.status} round={a.current_round} />
                    <ArrowRight size={16} weight="bold" className="opacity-30 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
